import {Trans} from '@lingui/macro';
import React, {FC} from 'react';

import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {styled} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {openInBrowser} from '../../../../../srcCommon/browser/openInBrowser';
import expressionStartingPointImage from '../../../../assets/images/expressionStartingPoint.png';
import {ExpressionsPicker} from '../../../editors/expressions/expressionsPicker';
import {ExpressionDependencies} from '../../../editors/expressions/expressionState';
import {RuleTriggersTypesEnum} from '../../ruleTriggerModel';
import {EditorStateActions} from '../hooks/useEditorState';

/*
 * Props.
 */

interface RuleEditorSidePanelNewFieldProps {
  onAddExpression: EditorStateActions['onAddExpression'];
  expressionDependencies: ExpressionDependencies;

  triggers?: ReadonlyArray<RuleTriggersTypesEnum>;
}

/*
 * Styles.
 */

const StyledEditorSidePanelNewField = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;

  h2 {
    margin-top: 24px;
    margin-bottom: 8px;
    font-size: ${fontSizes.veryLarge};
    color: ${(p) => p.theme.greys.shade80};
    font-weight: ${fontWeights.semibold};
  }

  p {
    margin-top: 0px;
    color: ${(p) => p.theme.greys.shade70};
    width: 310px;
  }

  img {
    width: 259px;
    margin-top: 24px;
    margin-bottom: 24px;
  }
`;

const StyledButton = styled(Button)`
  width: fit-content;
`;

const StyledLearnMoreButtonContent = styled.div`
  display: grid;
  align-items: center;
  grid-template-columns: min-content min-content;
  grid-gap: 4px;
`;

/*
 * Component.
 */

export const RuleEditorSidePanelNewField: FC<RuleEditorSidePanelNewFieldProps> = ({
  onAddExpression,
  triggers,
  expressionDependencies,
}) => (
  <StyledEditorSidePanelNewField>
    <h2>
      <Trans id="rule-editor-side-panel-new-field-title">Starting creating a dynamic variable</Trans>
    </h2>
    <p>
      <Trans id="rule-editor-side-panel-new-field-explanation">
        Build the step-by-step path to the information that you need to start creating smart rules.
      </Trans>
    </p>
    <Button
      variant={ButtonVariantsEnum.LINK}
      onClick={() => openInBrowser('https://help.front.com/t/y4hg1ck/smart-rules')}
    >
      <StyledLearnMoreButtonContent>
        <Trans id="upsell-button-learn-more">Learn more</Trans>
        <Icon name="externalLinkSmall" />
      </StyledLearnMoreButtonContent>
    </Button>
    <img src={expressionStartingPointImage} alt="" />
    <ExpressionsPicker
      onAddExpression={onAddExpression}
      triggers={triggers}
      dependencies={expressionDependencies}
    >
      <StyledButton variant={ButtonVariantsEnum.PRIMARY}>
        <Trans id="rule-editor-side-panel-starting-point-title">Start dynamic variable path</Trans>
      </StyledButton>
    </ExpressionsPicker>
  </StyledEditorSidePanelNewField>
);
